import { Box } from '@mui/material'
import { type GridEventListener, type GridColDef } from '@mui/x-data-grid'
import { Page } from 'components/Page'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { DataTable } from 'components/Shared/DataTable'
import { ErrorIndicator } from 'components/Shared/ErrorIndicator'
import { PageHeader } from 'components/Shared/PageHeader'
import { NoDataIndicator } from 'components/Shared/NoDataIndicator'
import { useGetMe } from 'hooks/api/useGetMe'
import { formatFullDate } from 'utils/util'
// import { useNavigate } from 'react-router-dom'
import { useCurrentCorporateAccountStore } from 'stores/useCurrentCorporateAccountStore'
import _ from 'lodash'
import { useLayout } from 'hooks/useLayout'
import { type SweepstakeStatusType } from 'src/types/api'
import { getLEIdsByPermission } from 'utils/permissions'
import {
  SweepstakesStatusLabel,
  sweepstakeStatusString,
} from 'pages/Sweepstakes/SweepstakesStatusLabel'
import { useGetSweepstakesByTypeWithGrouping } from 'hooks/api/useGetSweepstakesByTypeWithGrouping'
import { useNavigate } from 'react-router-dom'

const columns: GridColDef[] = [
  {
    field: 'name',
    headerName: 'Sweepstakes Name',
    minWidth: 400,
    flex: 1,
  },
  {
    field: 'state',
    headerName: 'Sweepstake Status',
    minWidth: 300,
    renderCell: (params) => {
      const status = params.row.sweepstakeData?.state ?? 'Draft'
      return <SweepstakesStatusLabel status={status} />
    },
  },
  {
    field: 'startDate',
    headerName: 'Start',
    minWidth: 150,
    renderCell: (params) => <Box>{formatFullDate(params.value)}</Box>,
  },
  {
    field: 'endDate',
    headerName: 'End',
    minWidth: 150,
    renderCell: (params) => <Box>{formatFullDate(params.value)}</Box>,
  },
]

const columnsMobile: Array<GridColDef<any>> = [
  {
    field: 'name',
    headerName: 'Sweepstakes Name',
    minWidth: 400,
    flex: 1,
  },
  {
    field: 'state',
    headerName: 'Sweepstake Status',
    minWidth: 300,
    valueGetter: (params: {
      row: { sweepstakeData: { state: SweepstakeStatusType } }
    }) => sweepstakeStatusString[params.row.sweepstakeData.state],
  },
  {
    field: 'startDate',
    headerName: 'Start',
    minWidth: 150,
    valueGetter: (params: { row: { startDate: string } }) =>
      params.row.startDate,
    valueFormatter: ({ value }: { value: string }) => formatFullDate(value),
  },
  {
    field: 'endDate',
    headerName: 'End',
    minWidth: 150,
    valueGetter: (params: { row: { endDate: string } }) => params.row.endDate,
    valueFormatter: ({ value }: { value: string }) => formatFullDate(value),
  },
]

export const LocationSweepstakesPage = () => {
  const { isMobile } = useLayout()
  const meQuery = useGetMe()
  const query = useGetSweepstakesByTypeWithGrouping({
    type: 'LocationSweepstake',
  })
  const navigate = useNavigate()
  const customOrder = {
    Enrollable: 1,
    Live: 2,
    InternalValidation: 3,
    WinnerValidation: 4,
    EnrollmentClosed: 5,
    Draft: 6,
    Closed: 7,
  }

  const selectRow: GridEventListener<'rowClick'> = (params) => {
    if (params.id !== null) {
      void navigate(`/LocationSweepstakes/${params.id}/Overview`)
    }
  }

  const selectedCorporateAccountId =
    useCurrentCorporateAccountStore().currentCorporateAccountId

  // Get LE Ids for the permission the user has access to
  const leIdsByPermission: number[] = getLEIdsByPermission(
    meQuery.data,
    selectedCorporateAccountId,
    'locationSweepstakesPermission'
  )

  // Filter data by permission
  const licensedEstablishmentsFiltered =
    meQuery?.data?.licensedEstablishments?.filter((x) =>
      leIdsByPermission.includes(Number(x.id))
    )

  // Filter the sweepstakes data based on the selected corporate account
  const filteredData: typeof query.data = []
  query.data?.forEach((record) => {
    const hasPermission = record.licensedEstablishments?.some((le) =>
      leIdsByPermission.includes(le.id)
    )

    const isCorporateAccount = record.licensedEstablishments?.some(
      (le) => le.corporateAccountId === selectedCorporateAccountId
    )

    if ((record.organizationId && hasPermission) ?? isCorporateAccount) {
      filteredData.push(record)
    }
  })

  // Filter licensed establishments based on the user's permissions
  const filteredDataWithLEs = filteredData?.map((record) => ({
    ...record,
    licensedEstablishments: record.licensedEstablishments?.filter((le) =>
      leIdsByPermission.includes(le.id)
    ),
  }))

  const sortedArray = _.orderBy(
    filteredDataWithLEs,
    (item) => customOrder[item.sweepstakeData?.state ?? 'Draft'],
    ['asc']
  )

  return (
    <Page
      header={
        <Box>
          <PageHeader
            title="Location Sweepstakes"
            isSecondary={true}
            headerBgColor="white"
          />
        </Box>
      }
    >
      {meQuery.isPending && <ActivityIndicator />}
      {meQuery.isError && <ErrorIndicator />}
      {licensedEstablishmentsFiltered?.length === 0 ? (
        <NoDataIndicator
          noImage
          noDataMessage="Your current permissions do not allow access to view location sweepstakes. To obtain access, please contact your Account Manager for assistance."
        />
      ) : (
        <>
          {query.isPending && <ActivityIndicator />}
          {query.isError && <p>An error occurred.</p>}
          {!query.isError && query.data && (
            <DataTable
              columns={isMobile ? columnsMobile : columns}
              rows={sortedArray}
              onCellClick={selectRow}
              onRowClick={selectRow} // handle row click for mobile where cell click does not work
            />
          )}
        </>
      )}
    </Page>
  )
}
